import { always } from "ramda";
import { createContext, useContext, useMemo } from "react";
import { useDevMode } from "../../hooks/useDevMode";
import { useStaticCallback } from "../../hooks/useStaticCallback.hook";
import { useMyMutation, useMyQuery } from "../Authorization/reactQueryWrappers";
import { useVisualEffects } from "../VisualEffects/VisualEffectsProvider";

// eslint-disable-next-line no-unused-vars
const MOCK_COMPLETED = {
  completedSession: true,
  completedShortTermGoal: true,
  watchedVideo: true,
  badges: {
    JANUARY: true,
    FEBRUARY: true,
    MARCH: true,
    APRIL: false,
    MAY: true,
    JUNE: true,
    JULY: true,
    AUGUST: true,
    SEPTEMBER: true,
    OCTOBER: false,
    NOVEMBER: true,
    DECEMBER: true,
  },
};
const MOCK_INCOMPLETED = {
  completedSession: false,
  completedShortTermGoal: false,
  watchedVideo: false,
  badges: {
    JANUARY: true,
    FEBRUARY: true,
    MARCH: true,
    APRIL: false,
    MAY: false,
    JUNE: false,
    JULY: false,
    AUGUST: false,
    SEPTEMBER: false,
    OCTOBER: false,
    NOVEMBER: false,
    DECEMBER: false,
  },
};

const useUserBadgesQuery = ({
  currentMonthIndex = new Date().getMonth(),
} = {}) => {
  const [isDevMode] = useDevMode();
  return useMyQuery({
    queryKey: ["user-badges", currentMonthIndex],
    fetchDef: {
      url: `/api/latest/user-badges`,
      to: isDevMode ? always(MOCK_INCOMPLETED) : undefined,
    },
  });
};

const GamificationContext = createContext({});

export const GamificationProvider = ({ children }) => {
  const { triggerBriefConfetti } = useVisualEffects();
  const currentMonthIndex = new Date().getMonth();

  const userBadgesQuery = useUserBadgesQuery({ currentMonthIndex });
  const watchedVideoMutation = useMyMutation({
    fetchDef: {
      method: "POST",
      url: `/api/latest/user-badges/watched-video`,
    },
    invalidate: [{ queryKey: ["user-badges"] }],
  });

  const handleGoalCompleted = useStaticCallback(() => {
    // https://topleader.atlassian.net/browse/TOP-318
    // if (!userBadgesQuery.data?.completedShortTermGoal)
    triggerBriefConfetti();
  });
  const handleSessionCompleted = useStaticCallback(() => {
    if (!userBadgesQuery.data?.completedSession) triggerBriefConfetti();
  });
  const handleVideoWatched = useStaticCallback(() => {
    watchedVideoMutation.mutate();
    if (!userBadgesQuery.data?.watchedVideo) {
      triggerBriefConfetti();
    }
  });

  const ctx = useMemo(
    () => ({
      userBadgesQuery,
      currentMonthIndex,
      handleGoalCompleted,
      handleSessionCompleted,
      handleVideoWatched,
    }),
    [
      currentMonthIndex,
      handleGoalCompleted,
      handleSessionCompleted,
      handleVideoWatched,
      userBadgesQuery,
    ],
  );

  return (
    <GamificationContext.Provider value={ctx}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => useContext(GamificationContext);
